if (!customElements.get("embla-slider")) {
  customElements.define(
    "embla-slider",
    class EmblaSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.emblaConfig = JSON.parse(this.getAttribute("embla-config")) || {
          loop: false,
          dragFree: true,
        };
        this.emblaApi = EmblaCarousel(this, this.emblaConfig, [
          EmblaCarouselWheelGestures(),
        ]);
      }
    },
  );
}

if (!customElements.get("embla-dots")) {
  customElements.define(
    "embla-dots",
    class EmblaDots extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.emblaApiMain = document.getElementById(
          this.getAttribute("embla-id"),
        ).emblaApi;

        if (!this.emblaApiMain) {
          throw new Error("Please provide an embla-id attribute");
        }
        this.initDots();
      }

      initDots = () => {
        const dotTemplate = this.querySelector("template").innerHTML;
        this.innerHTML = this.emblaApiMain
          .scrollSnapList()
          .map(() => dotTemplate)
          .join("");
        this.dotNodes = this.querySelectorAll("button");
        this.dotNodes.forEach((dot, index) => {
          dot.addEventListener("click", () => this.scrollTo(index), false);
        });

        this.emblaApiMain
          .on("init", this.toggleDotBtnsActive)
          .on("reInit", this.toggleDotBtnsActive)
          .on("select", this.toggleDotBtnsActive);
      };

      toggleDotBtnsActive = () => {
        const previous = this.emblaApiMain.previousScrollSnap();
        const selected = this.emblaApiMain.selectedScrollSnap();
        this.dotNodes[previous].classList.remove("selected");
        this.dotNodes[selected].classList.add("selected");
      };

      scrollTo = (index) => {
        this.emblaApiMain.scrollTo(index);
      };
    },
  );
}

if (!customElements.get("embla-thumb")) {
  customElements.define(
    "embla-thumb",
    class EmblaThumb extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.emblaApiMain = document.getElementById(
          this.getAttribute("embla-id"),
        ).emblaApi;

        if (!this.emblaApiMain) {
          throw new Error("Please provide an embla-id attribute");
        }

        this.thumbConfig = JSON.parse(this.getAttribute("thumb-config")) || {
          containScroll: "keepSnaps",
          dragFree: true,
        };
        this.initThumbs();
      }

      initThumbs = () => {
        const emblaThumbApi = EmblaCarousel(this, this.thumbConfig, [
          EmblaCarouselWheelGestures(),
        ]);
        const removeThumbBtnsClickHandlers = this.addThumbBtnsClickHandlers(
          this.emblaApiMain,
          emblaThumbApi,
        );
        const removeToggleThumbBtnsActive = this.addToggleThumbBtnsActive(
          this.emblaApiMain,
          emblaThumbApi,
        );

        this.emblaApiMain
          .on("destroy", removeThumbBtnsClickHandlers)
          .on("destroy", removeToggleThumbBtnsActive);
        emblaThumbApi
          .on("destroy", removeThumbBtnsClickHandlers)
          .on("destroy", removeToggleThumbBtnsActive);
      };

      addThumbBtnsClickHandlers = (emblaApiMain, emblaApiThumb) => {
        const slidesThumbs = emblaApiThumb.slideNodes();

        const scrollToIndex = slidesThumbs.map(
          (_, index) => () => emblaApiMain.scrollTo(index),
        );

        slidesThumbs.forEach((slideNode, index) => {
          slideNode.addEventListener("click", scrollToIndex[index], false);
        });

        return () => {
          slidesThumbs.forEach((slideNode, index) => {
            slideNode.removeEventListener("click", scrollToIndex[index], false);
          });
        };
      };

      addToggleThumbBtnsActive = (emblaApiMain, emblaApiThumb) => {
        const slidesThumbs = emblaApiThumb.slideNodes();

        const toggleThumbBtnsState = () => {
          emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap());
          const previous = emblaApiMain.previousScrollSnap();
          const selected = emblaApiMain.selectedScrollSnap();
          slidesThumbs[previous].classList.remove("thumbs-selected");
          slidesThumbs[selected].classList.add("thumbs-selected");
        };

        emblaApiMain.on("select", toggleThumbBtnsState);
        emblaApiThumb.on("init", toggleThumbBtnsState);

        return () => {
          const selected = emblaApiMain.selectedScrollSnap();
          slidesThumbs[selected].classList.remove("thumbs-selected");
        };
      };
    },
  );
}

if (!customElements.get("embla-controls")) {
  customElements.define(
    "embla-controls",
    /**
     * EmblaControls Web Component
     * Provides prev/next navigation buttons for an Embla carousel.
     * Supports hiding buttons at boundaries when hide-at-bounds attribute is set.
     *
     * @attribute {string} embla-id - ID of the target embla-slider element
     * @attribute {boolean} hide-at-bounds - When present, hides buttons when can't scroll further
     */
    class EmblaControls extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.targetEmblaSlider = document.getElementById(
          this.getAttribute("embla-id"),
        );
        if (!this.targetEmblaSlider) {
          throw new Error("Please provide an embla-id attribute");
        }
        this.hideAtBounds = this.hasAttribute("hide-at-bounds");
        this.initControls();
      }

      /**
       * Initializes the control buttons and sets up event listeners.
       */
      initControls = () => {
        this.prevButton = this.querySelector('[data-action="prev"]');
        this.nextButton = this.querySelector('[data-action="next"]');

        if (!this.prevButton && !this.nextButton) {
          throw new Error("Please specify prev and next buttons");
        }

        this.setupEventListeners();
      };

      /**
       * Sets up click handlers and Embla event subscriptions.
       */
      setupEventListeners = () => {
        const emblaApi = this.targetEmblaSlider.emblaApi;

        if (this.prevButton) {
          this.prevButton.addEventListener("click", () => {
            emblaApi.scrollPrev();
          });
        }

        if (this.nextButton) {
          this.nextButton.addEventListener("click", () => {
            emblaApi.scrollNext();
          });
        }

        if (this.hideAtBounds) {
          this.toggleButtonsVisibility(emblaApi);
          emblaApi.on("select", this.toggleButtonsVisibility);
          emblaApi.on("reInit", this.toggleButtonsVisibility);
        }
      };

      /**
       * Toggles visibility of prev/next buttons based on scroll position.
       * @param {Object} emblaApi - The Embla carousel API instance
       */
      toggleButtonsVisibility = (emblaApi) => {
        if (this.prevButton) {
          this.prevButton.classList.toggle(
            "td-embla-controls__btn--hidden",
            !emblaApi.canScrollPrev(),
          );
        }
        if (this.nextButton) {
          this.nextButton.classList.toggle(
            "td-embla-controls__btn--hidden",
            !emblaApi.canScrollNext(),
          );
        }
      };
    },
  );
}

if (!customElements.get("variant-select")) {
  customElements.define(
    "variant-select",
    class VariantSelect extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.productForm =
          this.closest("form") ||
          document.getElementById(this.getAttribute("form-id"));
        if (!this.productForm) {
          throw new Error("Please use this component inside a product form");
        }
        this.masterSelectEl =
          this.productForm.querySelector("select[name='id']");
        this.init();
      }

      init = () => {
        this.addEventListener("change", this.updateVariantId);
      };

      updateVariantId = () => {
        const selectedOptions = this.querySelectorAll("input:checked, select");
        const selectedValue = [...selectedOptions]
          .map((option) => option.value.toLowerCase().replace(/\s+/g, ""))
          .join("/");

        Array.from(this.masterSelectEl.options).forEach((option) => {
          const normalizedOptionValue = option.text
            .toLowerCase()
            .replace(/\s+/g, "");
          if (normalizedOptionValue === selectedValue) {
            option.selected = true;
          }
        });
      };
    },
  );
}
